import backgroundImage from "assets/images/jpg/SignUpPageMask.jpg";
import LogoImage from "assets/images/png/SignInLogo.png";
import {
  ViewerContext,
  localStorage,
  toast,
  useCheckIfSubscriptionFeatureActiveBroker,
} from "dash-ui";
import { RoutesPath } from "flow-components/routes/routes";
import {
  GetAccountDocument,
  GetCurrentBrokerDocument,
  useGetAccountLazyQuery,
  useGetAccountQuery,
  useSendEmailVerificationCodeMutation,
  useValidatePhoneNumberMutation,
} from "gql/generated.graphql";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";

type ConfirmCodeFormData = {
  code: string;
};

export const CodeConfirmationPage: FunctionComponent = () => {
  // React states
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();

  // Hooks
  const confirmCodeForm = useForm<ConfirmCodeFormData>();
  const navigate = useNavigate();
  const { account, broker, setAccount } = useContext(ViewerContext);

  // Queries and mutations
  const [getAccount] = useGetAccountLazyQuery();
  const [sendEmailVerificationMutation] =
    useSendEmailVerificationCodeMutation();
  const [validatePhoneNumberMutation] = useValidatePhoneNumberMutation();

  // Resend code functionality
  const onReSendCode = async () => {
    sendEmailVerificationMutation({
      onCompleted: async () => {
        toast.success("Email resent!");
      },
    });
  };

  // Code confirmation functionlaity
  const onConfirmCode = async (formData: ConfirmCodeFormData) => {
    setLoadingSubmit(true);

    try {
      await validatePhoneNumberMutation({
        variables: { code: formData.code },
        refetchQueries: [GetAccountDocument, GetCurrentBrokerDocument],
      });
      // reload window to ensure GQL Subscription works properly with new authorization header.
      window.open(RoutesPath.CompleteSignUpAddAdditionalInfo, "_self");
      return;
    } catch (error: any) {
      if (error.message === "WRONG_VERIFICATION_CODE") {
        confirmCodeForm.setError("code", { message: "Invalid Code." });
      }
      setLoadingSubmit(false);
      return;
    }
  };

  useEffect(() => {
    // Redirect SSO users to correct page
    if (account?.ssoId && !broker) {
      navigate(RoutesPath.CompleteSignUpSSO);
    }
  }, []);

  // Return HTML component
  return (
    <div className="max-w-screen w-screen h-screen flex bg-slate-50">
      <div className="flex w-0 lg:w-5/12 bg-sky-600 items-center justify-center relative">
        <img
          src={backgroundImage}
          alt="People at Dash"
          className="absolute h-full w-full object-cover"
        />
        <img src={LogoImage} alt="Dash Logo" className="z-10" />
      </div>

      <div className="flex w-full lg:w-7/12 justify-center max-h-full overflow-auto p-4 my-auto">
        <form
          id="code-confirmation-form"
          onSubmit={confirmCodeForm.handleSubmit(onConfirmCode)}
          className="code-confirmation-form flex flex-col gap-6 max-w-lg text-slate-900">
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-[Montserrat]">Confirm Your Email</h1>

            <p className="text-slate-500">
              Please enter the verification code sent to your email:{" "}
              {account?.email}.
            </p>
          </div>

          <div className="flex flex-col">
            <label htmlFor="code" className="text-xs text-slate-500">
              Confirmation Code
            </label>

            <input
              type="text"
              className={` ${
                confirmCodeForm.formState.errors.code?.message
                  ? "border-red-600"
                  : "border-slate-400"
              } flex items-center h-10 rounded px-3 border `}
              {...confirmCodeForm.register("code")}
              required
              autoFocus
            />

            <span className="text-red-600 text-sm">
              {confirmCodeForm.formState.errors.code?.message}
            </span>
          </div>

          <button
            type="submit"
            className="flex h-10 px-6 bg-sky-600 text-white rounded items-center justify-center disabled:opacity-30"
            disabled={loadingSubmit}>
            Confirm Your Code
          </button>

          <p className="mt-4">
            Didn't get the email? <br />
            Check your Spam folder and{" "}
            <button onClick={onReSendCode} className="text-sky-700">
              re-send the code
            </button>
            .
          </p>
        </form>
      </div>
    </div>
  );
};
